import { isEqual, isFunction } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AESEncryptURL } from "utils/encryption/AESEncrypt";
import { initialFilter } from "./ScheduleUIHelpers";

// REDUX
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { getListSchedule } from "../../redux/schedule";

// CONTEXT
import useAuth from "../../hooks/useAuth";

const ScheduleUIContext = createContext();

export const useScheduleUIContext = () => {
  return useContext(ScheduleUIContext);
};

export const ScheduleUIConsumer = ScheduleUIContext.Consumer;

export function ScheduleUIProvider({ children }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { authData } = useAuth();
  const { getListScheduleData } = useSelector(
    (state) => ({
      getListScheduleData: state.GetListScheduleData,
    }),
    shallowEqual
  );
  const { data, isLoading } = getListScheduleData;
  const [queryParams, setQueryParamsBase] = useState(initialFilter);

  useEffect(() => {
    dispatch(getListSchedule(queryParams));
  }, [dispatch, queryParams]);

  const setQueryParams = useCallback((nextQueryParams) => {
    setQueryParamsBase((prevQueryParams) => {
      if (isFunction(nextQueryParams)) {
        nextQueryParams = nextQueryParams(prevQueryParams);
      }
      if (isEqual(prevQueryParams, nextQueryParams)) {
        return prevQueryParams;
      }
      return nextQueryParams;
    });
  }, []);

  const dataUIEvents = {
    goToAddQuotaSchedule: () => {
      navigate(`/schedule/add-quota`, {
        state: {
          showDialog: true,
        },
      });
    },
    goToRemoveSchedule: (id, data) => {
      const secureID = AESEncryptURL(id.toString());
      navigate(`/schedule/${secureID}/remove`, {
        state: {
          showDialog: true,
          scheduleData: data,
        },
      });
    },
    goToScheduleEdit: (id) => {
      navigate(`/schedule/${id}/edit`, {
        state: {
          showDialog: true,
        },
      });
    },
    goToCancelSchedule: (id, data) => {
      const secureID = AESEncryptURL(id.toString());
      navigate(`/schedule/${secureID}/cancel`, {
        state: {
          showDialog: true,
          scheduleData: data,
        },
      });
    },
    goToSettingSchedule: (id) => {
      const secureID = AESEncryptURL(id.toString());
      navigate(`/schedule/${secureID}/setting`, {
        state: {
          showDialog: true,
        },
      });
    },
    goToCustomerDetail: (id, data) => {
      const secureID = AESEncryptURL(id.toString());
      navigate(`/customer/${secureID}/detail`, {
        state: {
          showDialog: true,
          scheduleData: data,
        },
      });
    },
    onHide: () => {
      navigate(-1);
    },
  };

  const handleResetData = () => {
    dispatch(getListSchedule(authData));
  };

  const value = {
    data,
    authData,
    queryParams,
    isLoadingData: isLoading,
    setQueryParams,
    setQueryParamsBase,
    handleResetData,
    goToAddQuotaSchedule: dataUIEvents.goToAddQuotaSchedule,
    goToScheduleEdit: dataUIEvents.goToScheduleEdit,
    goToSettingSchedule: dataUIEvents.goToSettingSchedule,
    goToCancelSchedule: dataUIEvents.goToCancelSchedule,
    goToRemoveSchedule: dataUIEvents.goToRemoveSchedule,
    goToCustomerDetail: dataUIEvents.goToCustomerDetail,
    onHide: dataUIEvents.onHide,
  };

  return (
    <ScheduleUIContext.Provider value={value}>
      {children}
    </ScheduleUIContext.Provider>
  );
}
